<template>
  <slot />
</template>

<script setup lang="ts">
import { computed, onMounted, provide } from 'vue';
import { watchArray } from '@vueuse/core';
import {
  type UseTrackingComposable,
  trackingInjectKey, type EventName,
} from './useTracking';
import { useCheckoutStore } from '../../stores/checkout';
import { Checkout } from '@vp/common';
import { trackEventIdDimension } from './eventDataLayer';
import { metaCommand, redditCommand, tikTokCommand } from './trackingPayloads';
import type { FbqStub, RdtStub, TtqStub } from '../../types/global';

onMounted(() => {
  const script = document.createElement('script');
  script.setAttribute('src', 'https://www.googletagmanager.com/gtag/js');
  document.head.appendChild(script);
});

const checkoutStore = useCheckoutStore();
const trackingEntries = computed<Checkout.TrackingEntry.TrackingEntry[]>(
  () => checkoutStore.getEventCheckoutConfig?.tracking || [],
);

// Attributes the container's hits to this event. Deliberately outside the payloads
// below: those reach each client's own properties, which must not see our dimension.
// The seed in +onCreateApp.ts covers the page view that fires before this mounts.
trackEventIdDimension(() => checkoutStore.getSelectedEvent?.id);

function gtag(..._args: unknown[]) {
  if (!import.meta.env.SSR) {
    window.dataLayer.push(arguments);
  }
}

if (!import.meta.env.SSR) {
  window.dataLayer = window.dataLayer || [];
  gtag('js', new Date());

  /* eslint-disable @stylistic/multiline-ternary, @stylistic/operator-linebreak, @typescript-eslint/no-unused-expressions */
  (function (f: { fbq?: FbqStub; _fbq?: FbqStub }, b, e: 'script', v) {
    if (f.fbq) {
      return;
    }
    const n: FbqStub = Object.assign(
      function () {
        n.callMethod
          ? n.callMethod.apply(n, arguments)
          : n.queue.push(arguments);
      },
      { queue: [] as IArguments[], loaded: true, version: '2.0' },
    );
    n.push = n;
    f.fbq = n;
    if (!f._fbq) {
      f._fbq = n;
    }
    const t = b.createElement(e);
    t.async = true;
    t.src = v;
    const s = b.getElementsByTagName(e)[0];
    s.parentNode?.insertBefore(t, s);
  })(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js');
  /* eslint-enable @stylistic/multiline-ternary, @stylistic/operator-linebreak, @typescript-eslint/no-unused-expressions */

  (function (w: { TiktokAnalyticsObject: string; ttq?: TtqStub }, d, t: 'ttq') {
    w.TiktokAnalyticsObject = t;
    const ttq = (w[t] = w[t] || ([] as unknown as TtqStub));
    ttq.methods = [
      'page',
      'track',
      'identify',
      'instances',
      'debug',
      'on',
      'off',
      'once',
      'ready',
      'alias',
      'group',
      'enableCookie',
      'disableCookie',
      'holdConsent',
      'revokeConsent',
      'grantConsent',
    ];
    ttq.setAndDefer = function (obj: any, method: string) {
      obj[method] = function () {
        obj.push([method].concat(Array.prototype.slice.call(arguments, 0)));
      };
    };
    for (let i = 0; i < ttq.methods.length; i++) {
      ttq.setAndDefer(ttq, ttq.methods[i]);
    }
    ttq.instance = function (id: string) {
      const inst: any[] = (ttq._i[id] = ttq._i[id] || []);
      for (let i = 0; i < ttq.methods.length; i++) {
        ttq.setAndDefer(inst, ttq.methods[i]);
      }
      return inst;
    };
    ttq.load = function (id: string) {
      const script = d.createElement('script');
      script.type = 'text/javascript';
      script.async = true;
      script.src =
        'https://analytics.tiktok.com/i18n/pixel/events.js?sdkid=' +
        id +
        '&lib=' +
        t;
      const first = d.getElementsByTagName('script')[0];
      first.parentNode!.insertBefore(script, first);
      ttq._i = ttq._i || {};
      ttq._i[id] = [];
      ttq._t = ttq._t || {};
      ttq._t[id] = +new Date();
    };
  })(window, document, 'ttq');

  (function (w, d) {
    if (!w.rdt) {
      const p: RdtStub = Object.assign(
        function () {
          // eslint-disable-next-line @typescript-eslint/no-unused-expressions
          p.sendEvent ? p.sendEvent.apply(p, arguments) : p.callQueue.push(arguments);
        },
        { callQueue: [] as IArguments[] },
      );
      w.rdt = p;
      const t = d.createElement('script');
      t.src = 'https://www.redditstatic.com/ads/pixel.js';
      t.async = true;
      const s = d.getElementsByTagName('script')[0];
      s.parentNode?.insertBefore(t, s);
    }
  })(window, document);

  watchArray(trackingEntries, (_new, _old, added, _removed) => {
    added.forEach(entry => {
      switch (entry.provider) {
        case 'ga':
          gtag('config', entry.trackingId);
          break;
        case 'facebook':
          window.fbq('init', entry.trackingId);
          window.fbq('track', 'PageView');
          break;
        case 'tik_tok':
          window.ttq?.load?.(entry.trackingId);
          window.ttq?.page?.();
          break;
        case 'reddit':
          window.rdt?.('init', entry.trackingId);
          window.rdt?.('track', 'PageVisit');
          break;
      }
    });
  }, { immediate: true });
}

const fbTrack = (...args: any[]) => {
  if (import.meta.env.SSR) {
    return;
  }

  try {
    window.fbq(...args);
  } catch {
    // NOP
  }
};

const ttTrack = (...args: any[]) => {
  if (import.meta.env.SSR || !window.ttq) {
    return;
  }

  try {
    window.ttq.track?.(...args);
  } catch {
    // NOP
  }
};

const rdtTrack = (...args: any[]) => {
  if (import.meta.env.SSR || !window.rdt) {
    return;
  }

  try {
    window.rdt?.(...args);
  } catch {
    // NOP
  }
};

const track = (...args: any[]) => {
  gtag(...args);
};

// GA4 gets the payload verbatim; all three pixels get one built for them, so the event
// reaches their catalogs as a product. See trackingPayloads.ts for why one object cannot
// serve all four.
const trackEvent = (event: EventName, data: unknown) => {
  try {
    gtag('event', event, data);
  } catch {
    // NOP
  }

  // The same event the dataLayer dimension watches, so the id a catalog matches on and
  // the id GA4 is attributed to can never diverge.
  const eventId = checkoutStore.getSelectedEvent?.id;

  const meta = metaCommand(event, data, eventId);
  fbTrack(meta.command, meta.name, meta.payload);

  const tikTok = tikTokCommand(event, data, eventId);
  ttTrack(tikTok.name, tikTok.payload);

  const reddit = redditCommand(event, data, eventId);
  rdtTrack('track', reddit.name, reddit.payload);
};

provide<UseTrackingComposable>(trackingInjectKey, {
  track,
  trackEvent,
});
</script>
